import axios from "axios";
import { debug, formatSafeErrorMessage } from "../../../utils/debugLogger";

// Use environment variables with fallbacks for API URLs
const DEV_USER_URL = "https://n4kl7g5v9i.execute-api.us-east-1.amazonaws.com/Prod/users";
const PROD_USER_URL = "https://customers.api.sagelyn.ai/users";

const BASE_URL = process.env.REACT_APP_CUSTOMER_API_URL
  ? process.env.REACT_APP_CUSTOMER_API_URL.replace(/customers$/, "users")
  : (process.env.REACT_APP_ENV === "development" ? DEV_USER_URL : PROD_USER_URL);

function userApiErrorMessage(error: unknown): string {
  const err = error as {
    response?: {
      status?: number;
      data?: { error?: string; code?: string; detail?: string; message?: string };
    };
  };
  const status = err.response?.status;
  const data = err.response?.data;
  if (data && (data.error || data.code || data.detail || data.message)) {
    const segments: string[] = [];
    if (data.error) segments.push(data.error);
    if (data.message && data.message !== data.error) segments.push(data.message);
    if (data.code) segments.push(`[${data.code}]`);
    if (data.detail) segments.push(data.detail);
    const msg = segments.join(" ").trim();
    if (msg) {
      return status !== undefined ? `HTTP ${status}: ${msg}` : msg;
    }
  }
  return formatSafeErrorMessage(error);
}

export interface User {
  user_id: string;
  email: string;
  email_lc?: string; // lowercase email for GSI
  display_name: string;
  groups: string;  // Changed to string for API compatibility
  status: string;
  created_at: string;
  updated_at: string;
  subscription_tier?: string;
  subscription_status?: string;   // ACTIVE | NOT_ACTIVE | NONE
  subscription_product_name?: string;
  profile_created?: boolean;
  ui_theme_preference?: "light" | "dark" | "system";
}

/**
 * List all users (admin only)
 */
export const listUsers = async (idToken: string): Promise<User[]> => {
  try {
    const response = await axios.get(BASE_URL, {
      headers: {
        Authorization: idToken,
        "Content-Type": "application/json"
      }
    });
    
    let usersData: User[] = [];
    
    // Handle the specific response structure {"users": [...], "count": n}
    if (response.data && response.data.users && Array.isArray(response.data.users)) {
      usersData = response.data.users;
    }
    // Fallback to the raw response if it's an array
    else if (Array.isArray(response.data)) {
      usersData = response.data;
    }
    else {
      debug.warn("Unexpected response format from list users (expected users array)");
      return [];
    }
    
    // Normalize user groups to ensure they're always strings
    return usersData.map(user => {
      // If groups is an array, convert it to string (take first element)
      if (Array.isArray(user.groups)) {
        debug.log("Normalizing user groups from array to string", { userId: user.user_id });
        return {
          ...user,
          groups: user.groups[0] || "Customer" // Default to Customer if empty array
        };
      }
      return user;
    });
  } catch (error: unknown) {
    debug.error("Error in listUsers:", formatSafeErrorMessage(error));
    const err = error as { response?: { data?: { message?: string } } };
    if (err.response) {
      throw new Error(err.response.data?.message || "Failed to list users");
    }
    throw error;
  }
};

/**
 * Fetch a user by Cognito user ID
 */
export const fetchUserById = async (userId: string, idToken: string): Promise<User> => {
    // Check if userId already has the prefix
    const formattedUserId = userId.startsWith("user_id_") ? userId : `user_id_${userId}`;
    debug.log("fetchUserById", { userId: formattedUserId });
    const url = `${BASE_URL}/${formattedUserId}`;
  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
    });
    
    const userData = response.data as User;
    
    // Normalize user groups to ensure they're always strings
    if (Array.isArray(userData.groups)) {
      debug.log("Normalizing user groups from array to string", { userId: userData.user_id });
      userData.groups = userData.groups[0] || "Customer"; // Default to Customer if empty array
    }
    
    return userData;
  } catch (error: any) {
        if (error.response && error.response.status === 404) {
        throw new Error("User not found");
    }
    debug.error("Error fetching user:", formatSafeErrorMessage(error));
    throw error;
  }
};

/**
 * Update a user by Cognito user ID
 */
export const updateUser = async (user: User, idToken: string): Promise<User> => {
  const url = `${BASE_URL}/${user.user_id}`;
  
  debug.log("User update request", { userId: user.user_id });

  try {
    const response = await axios.put(url, user, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
    });
    return response.data as User;
  } catch (error) {
    debug.error("Error updating user:", formatSafeErrorMessage(error));
    throw error;
  }
};

/**
 * DELETE /users/{user_id} — SuperAdmin only. Body: none.
 * Self-delete returns 400 (CannotDeleteSelf).
 */
export const deleteUser = async (userId: string, idToken: string): Promise<void> => {
  const formattedUserId = userId.startsWith("user_id_") ? userId : `user_id_${userId}`;
  const url = `${BASE_URL}/${formattedUserId}`;
  try {
    await axios.delete(url, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
    });
  } catch (error: unknown) {
    debug.error("Error deleting user:", formatSafeErrorMessage(error));
    throw new Error(userApiErrorMessage(error));
  }
};

/**
 * Create a new user (if needed)
 */
export const createUser = async (user: Partial<User>, idToken: string): Promise<User> => {
  try {
    const response = await axios.post(BASE_URL, user, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
    });
    return response.data as User;
  } catch (error) {
    debug.error("Error creating user:", formatSafeErrorMessage(error));
    throw error;
  }
};


export const assignUserToCustomer = async (
  userId: string, 
  customerId: string, 
  role: string,
  idToken: string
): Promise<void> => {
  try {
    debug.log("assignUserToCustomer", { userId, customerId, role });

    const formattedUserId = userId.startsWith("user_id_") ? userId : `user_id_${userId}`;

    const payload = {
      customer_id: customerId,
      role: role
    };

    const response = await axios.post(
      `${BASE_URL}/${formattedUserId}/assignments`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json"
        }
      }
    );
    
    debug.log("Assignment request completed", { ok: true });
    return response.data;
  } catch (error: unknown) {
    debug.error("Error assigning user to customer:", formatSafeErrorMessage(error));
    const err = error as { response?: { data?: { message?: string } } };
    if (err.response) {
      throw new Error(err.response.data?.message || "Failed to assign user");
    }
    throw error;
  }
};